"use client";

import { FormEvent, useEffect, useState } from "react";
import { X } from "lucide-react";
import { postEnquiry } from "@/lib/db/property";
import type { UnitInterest } from "@/interfaces/propertyinterface";
import { Button } from "../Button";
import { reportLead } from "@/lib/metaPixel";

type Errors = Partial<Record<"name" | "mobile" | "email" | "consent" | "submit", string>>;

const inputClass =
  "mt-1.5 h-11 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100";

function normalizeMobile(value: string) {
  const local = value.replace(/[\s()-]/g, "").replace(/^\+?65/, "");
  return /^\d{8}$/.test(local) ? `+65${local}` : null;
}

export function ProjectEnquiryDialog({
  open,
  onClose,
  propertyId,
  propertyName,
  interest,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
  interest?: UnitInterest | null;
}) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (!open) return;
    setSent(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open, sending]);

  if (!open) return null;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedMobile = normalizeMobile(mobile);
    const next: Errors = {};
    if (!name.trim()) next.name = "Enter your name.";
    if (!normalizedMobile) next.mobile = "Enter a valid Singapore mobile number.";
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) next.email = "Enter a valid email address.";
    if (!consent) next.consent = "Please agree before continuing.";
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setSending(true);
    setErrors({});
    const result = await postEnquiry({
      name: name.trim(),
      email: email.trim(),
      mobile_number: normalizedMobile as string,
      brochure: false,
      price_list: true,
      flat_type_id: [],
      property: propertyId,
      source_url: window.location.href,
      unit_interest: interest ?? undefined,
    });
    setSending(false);
    if (typeof result === "string") {
      setErrors({ submit: result });
      return;
    }
    reportLead("Unit enquiry");
    setSent(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 sm:items-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !sending) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="project-enquiry-title" className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-6 sm:max-w-md sm:rounded-2xl">
        <button type="button" onClick={onClose} disabled={sending} aria-label="Close enquiry" className="absolute right-4 top-4 rounded-full p-2 text-ink-muted hover:bg-surface">
          <X aria-hidden="true" className="size-5" />
        </button>
        <h2 id="project-enquiry-title" className="pr-10 font-display text-2xl font-semibold text-ink">
          Enquire about {propertyName}
        </h2>
        {interest?.unit ? (
          <p className="mt-2 rounded-xl bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700">
            Home: {interest.unit}{interest.plan ? `, ${interest.plan}` : ""}{interest.type ? `, ${interest.type}` : ""}
          </p>
        ) : interest?.plan && (
          <p className="mt-2 rounded-xl bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700">
            Layout: {interest.plan}{interest.type ? `, ${interest.type}` : ""}
          </p>
        )}
        {sent ? (
          <div className="mt-6 rounded-xl bg-avail-soft p-4 text-sm text-avail">
            Thanks. Winnie will contact you about this project.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <Field label="Name" error={errors.name}>
              <input value={name} onChange={(event) => setName(event.target.value)} className={inputClass} autoComplete="name" />
            </Field>
            <Field label="Mobile" error={errors.mobile}>
              <input value={mobile} onChange={(event) => setMobile(event.target.value)} className={inputClass} inputMode="tel" autoComplete="tel" placeholder="+65 8877 2688" />
            </Field>
            <Field label="Email (optional)" error={errors.email}>
              <input value={email} onChange={(event) => setEmail(event.target.value)} className={inputClass} type="email" autoComplete="email" />
            </Field>
            <label className="flex cursor-pointer items-start gap-3 text-xs leading-5 text-ink-muted">
              <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} className="mt-0.5 size-4 rounded border-line text-brand-500 focus:ring-brand-500" />
              <span>I agree to be contacted about this project. My details are kept private.</span>
            </label>
            {errors.consent && <p className="text-xs text-sold">{errors.consent}</p>}
            {errors.submit && <p className="text-sm text-sold">{errors.submit}</p>}
            <Button type="submit" fullWidth disabled={sending}>
              {sending ? "Sending..." : "Send enquiry"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium text-ink">
      {label}
      {children}
      {error && <span className="mt-1 block text-xs font-normal text-sold">{error}</span>}
    </label>
  );
}
